/**
 * Chrome Bookmark HTML Parser
 * Parses Chrome's exported bookmark HTML file format
 * No external licenses required - uses standard HTML parsing
 */

/**
 * Parse Chrome bookmark HTML file content
 * @param {string} htmlContent - The raw HTML content from Chrome bookmarks export
 * @returns {Array} Array of bookmark objects
 */
export const parseChromeBookmarks = (htmlContent) => {
  const bookmarks = [];
  
  try {
    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Find all anchor tags (bookmarks)
    const anchors = doc.querySelectorAll('a[href]');
    
    anchors.forEach((anchor, index) => {
      const href = anchor.getAttribute('href');
      const title = anchor.textContent.trim();
      const addDate = anchor.getAttribute('add_date');
      const icon = anchor.getAttribute('icon');
      
      // Skip empty URLs or invalid bookmarks
      if (!href || !title || href.startsWith('javascript:')) {
        return;
      }
      
      // Extract folder path by traversing parent elements
      const folderPath = extractFolderPath(anchor);
      
      // Create description with folder path as tags
      const cleanFolderPath = folderPath.replace(/Lesezeichenleiste/g, 'Chrome');
      const description = cleanFolderPath ? `Tags: ${cleanFolderPath}` : 'Importiert aus Chrome';
      
      const bookmark = {
        title: title,
        url: sanitizeUrl(href),
        description: description,
        isFavorite: false,
        createdAt: addDate ? new Date(parseInt(addDate) * 1000).toISOString() : new Date().toISOString(),
        importedAt: new Date().toISOString(),
        source: 'chrome_import'
      };
      
      bookmarks.push(bookmark);
    });
    
    console.log(`📚 Parsed ${bookmarks.length} bookmarks from Chrome export`);
    return deduplicateBookmarks(bookmarks);
    
  } catch (error) {
    console.error('Error parsing Chrome bookmarks:', error);
    throw new Error('Fehler beim Parsen der Chrome-Lesezeichen: ' + error.message);
  }
};

/**
 * Extract folder path from bookmark's parent elements
 * @param {Element} anchor - The anchor element
 * @returns {string} The folder path (e.g., "Chrome > Work > Development")
 */
const extractFolderPath = (anchor) => {
  const folders = [];
  let currentElement = anchor.parentElement;
  
  // Traverse up the DOM tree to find folder names
  while (currentElement && currentElement.tagName !== 'HTML') {
    // Look for H3 elements that contain folder names
    if (currentElement.tagName === 'DL') {
      // Get the previous sibling that should be the folder name
      let prevSibling = currentElement.previousElementSibling;
      while (prevSibling) {
        if (prevSibling.tagName === 'H3') {
          let folderName = prevSibling.textContent.trim();
          // Replace "Lesezeichenleiste" with "Chrome"
          if (folderName === 'Lesezeichenleiste') {
            folderName = 'Chrome';
          }
          folders.unshift(folderName);
          break;
        }
        prevSibling = prevSibling.previousElementSibling;
      }
    }
    currentElement = currentElement.parentElement;
  }
  
  return folders.length > 0 ? folders.join(' > ') : '';
};

/**
 * Validate Chrome bookmark HTML file
 * @param {string} htmlContent - The HTML content to validate
 * @returns {boolean} True if valid Chrome bookmark file
 */
export const validateChromeBookmarkFile = (htmlContent) => {
  // Check for Chrome bookmark file markers
  const chromeMarkers = [
    'NETSCAPE-Bookmark-file-1',
    'automatically generated file',
    'DO NOT EDIT',
    '<TITLE>Bookmarks</TITLE>'
  ];
  
  return chromeMarkers.some(marker => 
    htmlContent.toUpperCase().includes(marker.toUpperCase())
  );
};

/**
 * Clean and sanitize bookmark URL
 * @param {string} url - The URL to clean
 * @returns {string} Cleaned URL
 */
export const sanitizeUrl = (url) => {
  try {
    if (!url || typeof url !== 'string') {
      return '';
    }
    
    url = url.trim();
    
    // Add protocol if missing
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('ftp://')) {
      url = 'https://' + url;
    }
    
    // Validate URL format
    new URL(url);
    
    return url;
  } catch (error) {
    console.warn('Invalid URL:', url);
    return '';
  }
};

/**
 * Remove duplicate bookmarks based on URL
 * @param {Array} bookmarks - Array of bookmark objects
 * @returns {Array} Deduplicated bookmarks
 */
export const deduplicateBookmarks = (bookmarks) => {
  const seen = new Set();
  const unique = [];
  
  bookmarks.forEach(bookmark => {
    const key = bookmark.url.toLowerCase();
    if (!seen.has(key) && bookmark.url) {
      seen.add(key);
      unique.push(bookmark);
    }
  });
  
  const duplicatesRemoved = bookmarks.length - unique.length;
  if (duplicatesRemoved > 0) {
    console.log(`🔄 Removed ${duplicatesRemoved} duplicate bookmarks`);
  }
  
  return unique;
};

/**
 * Get import statistics
 * @param {Array} bookmarks - Array of bookmark objects
 * @returns {Object} Import statistics
 */
export const getImportStats = (bookmarks) => {
  const folders = new Set();
  const domains = new Set();
  
  bookmarks.forEach(bookmark => {
    // Extract folder names from description
    if (bookmark.description.startsWith('Tags: ')) {
      const tags = bookmark.description.replace('Tags: ', '').split(' > ');
      tags.forEach(tag => folders.add(tag));
    }
    
    // Count unique domains
    try {
      const domain = new URL(bookmark.url).hostname;
      domains.add(domain);
    } catch (error) {
      // Invalid URL, skip domain counting
    }
  });
  
  return {
    totalBookmarks: bookmarks.length,
    uniqueFolders: folders.size,
    uniqueDomains: domains.size,
    importDate: new Date().toISOString()
  };
};